import React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useModal } from "@/contexts/ModalContext";

/**
 * ModalCard: Standard wrapper for all modal content.
 * - Enforces consistent styling.
 * - Always shows close button in the same place.
 * - Handles optional title and description.
 * - Accepts children for modal-specific content.
 *
 * Props:
 * - title: string (optional)
 * - description: string (optional)
 * - children: ReactNode (modal content)
 * - className: string (optional additional classes)
 * - variant: string (for future use)
 */
const ModalCard = ({
  title,
  description,
  children,
  className = "",
  variant = "default",
}) => {
  const { closeModal } = useModal();

  return (
    <div
      className={cn(
        // Standard modal styling
        "relative bg-stone-brown rounded-2xl shadow-2xl p-8 w-full max-w-lg",
        // Variant support (future)
        variant === "wide" && "max-w-2xl",
        className
      )}
    >
      {/* Close Button (always top-right) */}
      <button
        onClick={closeModal}
        className="absolute top-4 right-4 text-field-grey hover:text-imperial-red transition-colors"
        aria-label="Close"
        type="button"
      >
        <X className="w-6 h-6" />
      </button>

      {/* Header (title + optional description) */}
      {(title || description) && (
        <div className="mb-4">
          {title && (
            <h2 className="text-xl font-bold text-mikado-yellow font-poppins">
              {title}
            </h2>
          )}
          {description && (
            <p className="text-sm text-field-grey">{description}</p>
          )}
        </div>
      )}

      {/* Modal Content */}
      <div>
        {children}
      </div>
    </div>
  );
};

export default ModalCard;